// src/components/launch/CheckSpecPanel.jsx
//
// "What did the compile actually produce?" — the spec + editor for ONE check
// under a Launch criterion.
//
// Before this, a compiled criterion showed a check id, a confidence score and a
// verdict. What the check GRADED (which requests) and what it ASSERTED were
// invisible: a "Met" could not be audited, and a mis-scoped compile could only
// be fixed by re-compiling and hoping the model chose differently. This panel
// shows both halves in English (utils/describeCheck.js), exposes the raw Check
// JSON, and lets the tester edit it in place.
//
// Editing writes through POST /proxy/checks/suites/<suite>/append (replace by
// id) — the launch's own `launch-<id>` suite, which compileCriteria.js
// REPLACES wholesale on the next compile. That is the intended lifetime: an
// edit is a fix for THIS run, and re-compiling is explicitly a reset. The Save
// button says so.
//
// Never throws in render: every field is read defensively, since these objects
// come from the proxy's suite store, not from a typed model.

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { describeCheck } from '../../utils/describeCheck';
import { buildBaselineExport } from '../../utils/exportCheckForBaseline';
import { copyTextToClipboard } from '../../utils/clipboard';

const MONO = { fontFamily: 'monospace', fontSize: 11 };

function stringify(check) {
  try { return JSON.stringify(check, null, 2); } catch { return ''; }
}

// Clipboard helper — mirrors LaunchPage.jsx's copyTextToClipboard /
// ChecksPage.jsx's (no shared util exists for this yet across the three
// call sites; each one is small enough that a shared module hasn't been
// worth the indirection).

/**
 * props:
 *   check    — the Check object (from GET /proxy/launches/<id>/checks)
 *   onSave   — async (nextCheck) => void; rejects with err.body.issues on a
 *              validation failure. Omit to render read-only.
 */
export default function CheckSpecPanel({ check, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => stringify(check));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // "Export for PR" — the promotion path INTO the committed baseline. There
  // is deliberately no write here: conformance/suiteStore.js rejects any
  // write to a committed suite outright, and this button doesn't try to get
  // around that. It only formats the check + drafts a commit message, and
  // — the part that matters — surfaces app-specificity warnings BEFORE the
  // JSON is copied, since a promoted check that quietly bakes in one
  // publisher's IU/bundle/host/cust_params would undo the baseline's
  // app-agnostic design. copied tracks the button label; the export result
  // itself (json/commitMessage/warnings/error) stays visible once opened so
  // the warnings can't be missed by a re-render.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // A re-compile replaces the suite: whatever is on screen must follow the
  // new definition rather than keep showing the previous one as a draft.
  useEffect(() => {
    setDraft(stringify(check));
    setEditing(false);
    setError('');
    setExportOpen(false);
    setExportResult(null);
    setCopied(false);
  }, [check]);

  if (!check || typeof check !== 'object') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        This check id isn&apos;t in the launch&apos;s suite — it was cited from another suite, or the
        launch was compiled before check definitions were stored. Re-compile to resolve it.
      </Typography>
    );
  }

  const d = describeCheck(check);

  async function handleSave() {
    setSaving(true);
    setError('');
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      setError(`Not valid JSON: ${e?.message || e}`);
      setSaving(false);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('A check must be a JSON object.');
      setSaving(false);
      return;
    }
    if (parsed.id !== check.id) {
      // Save is a replace-by-id: a renamed check would be ADDED alongside the
      // original, and the criterion (which joins results by checkId) would keep
      // grading the old one.
      setError(`The id must stay "${check.id}" — the criterion maps to that id. Change the rule, not the id.`);
      setSaving(false);
      return;
    }
    try {
      await onSave(parsed);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      const issues = e?.body?.issues;
      setError(Array.isArray(issues) && issues.length ? issues.join('; ') : (e?.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }

  function handleExportToggle() {
    if (exportOpen) {
      setExportOpen(false);
      return;
    }
    // Recompute fresh each open — cheap, pure, and guarantees the warnings
    // shown always match the check currently on screen (not a stale draft).
    setExportResult(buildBaselineExport(check));
    setCopied(false);
    setExportOpen(true);
  }

  function handleCopyJson() {
    if (!exportResult?.json) return;
    setCopied(copyTextToClipboard(exportResult.json));
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Box sx={{ mt: 0.75, mb: 0.5, pl: 1, borderLeft: '2px solid var(--line)' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Chip size="small" label={`severity: ${d.severity}`} variant="outlined" />
        {d.absence && <Chip size="small" label="absence check" variant="outlined" />}
        {onSave && !editing && (
          <Button size="small" onClick={() => setEditing(true)}>Edit check</Button>
        )}
        <Button size="small" onClick={handleExportToggle}>
          {exportOpen ? 'Hide export' : 'Export for PR…'}
        </Button>
        {saved && <Typography variant="caption" sx={{ color: 'success.main' }}>Saved ✓ — re-run to apply</Typography>}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        <strong>Grades:</strong> {d.scope}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        <strong>Rule:</strong> {d.rule}
      </Typography>
      {d.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
          {d.description}
        </Typography>
      )}
      {d.why && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <strong>Why it matters:</strong> {d.why}
        </Typography>
      )}
      {d.fix && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <strong>If it fails:</strong> {d.fix}
        </Typography>
      )}

      {exportOpen && exportResult && (
        <Box sx={{ mt: 1, mb: 1 }}>
          {!exportResult.ok ? (
            <Alert severity="error" sx={{ py: 0 }}>
              Not shaped like a check — can&apos;t export: {exportResult.error}
            </Alert>
          ) : (
            <>
              {/* Warnings render BEFORE the copy button on purpose — an
                  app-specific check silently promoted into the app-agnostic
                  baseline is exactly the failure mode this button exists to
                  prevent, so the reviewer must see them before they can copy. */}
              {exportResult.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                    This looks app-specific, not spec-level — review before promoting:
                  </Typography>
                  {exportResult.warnings.map((w, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      • {w}
                    </Typography>
                  ))}
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Button size="small" variant="contained" onClick={handleCopyJson}>
                  {copied ? 'Copied ✓' : 'Copy JSON'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Formatted to match adtech-baseline.checks.json — paste as a new array element and open a PR. This
                  does not write anywhere; git review is still the gate.
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                <strong>Suggested commit message</strong>
              </Typography>
              <Box
                component="pre"
                sx={{ ...MONO, m: 0, mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', maxHeight: 160, whiteSpace: 'pre-wrap' }}
              >
                {exportResult.commitMessage}
              </Box>
              <Box
                component="pre"
                sx={{ ...MONO, m: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', maxHeight: 220 }}
              >
                {exportResult.json}
              </Box>
            </>
          )}
        </Box>
      )}

      {editing ? (
        <Box sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            size="small"
            inputProps={{ style: MONO, spellCheck: false }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} alignItems="center">
            <Button size="small" variant="contained" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save check'}
            </Button>
            <Button
              size="small"
              disabled={saving}
              onClick={() => { setDraft(stringify(check)); setEditing(false); setError(''); }}
            >
              Cancel
            </Button>
            <Typography variant="caption" color="text.secondary">
              Saved to this launch&apos;s suite — a re-compile replaces it.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{ ...MONO, mt: 0.75, mb: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', maxHeight: 260 }}
        >
          {stringify(check)}
        </Box>
      )}
    </Box>
  );
}
